import { Card, CardContent, CardHeader, Divider } from "@mui/material";
import { FormControl, InputLabel, ListItem, ListItemText, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { CompanySetting, restoreCompanySettings, updateCompanySettings, WarningLevel } from "../companySettingsAPI";
import { useAppDispatch } from "../../../../app/hooks";
import { showSuccess, showError } from "../../../notifications/notificationsSlice";
import { CardActionButtons } from "./CardActionButtons";
import { Localized } from "@fluent/react";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";


interface CompanyComputerizedSystemsSettingCardProps {
    companySettings: CompanySetting[];
    onUpdate: (updatedSettings: CompanySetting[]) => void;
}

const COMPUTERIZED_SYSTEM_WARNING_LEVELS: WarningLevel[] = ["INFO", "WARNING", "ERROR", "NONE"];

const COMPUTERIZED_SYSTEM_NUMBER_COMPANY_WARNING_LEVEL_CODE = "CS_NUMBER_NONUNIQUE_COMPANY_WARNING_LEVEL";
const COMPUTERIZED_SYSTEM_NUMBER_SUPPLIER_WARNING_LEVEL_CODE = "CS_NUMBER_NONUNIQUE_SUPPLIER_WARNING_LEVEL";
const COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_WARNING_LEVEL_CODE = "CS_NUMBER_NONUNIQUE_STRUCTURE_WARNING_LEVEL";
const COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE = "CS_NUMBER_NONUNIQUE_STRUCT_SUPP_WARNING_LEVEL";

const getWarningLevel = (setting: CompanySetting | undefined): WarningLevel => {
    const upperValue = setting?.value?.toUpperCase();

    if (upperValue === "INFO" || upperValue === "WARNING" || upperValue === "ERROR" || upperValue === "NONE") {
        return upperValue;
    }

    return "NONE";
};

export const CompanyComputerizedSystemsSettingCard: React.FC<CompanyComputerizedSystemsSettingCardProps> = ({ companySettings, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const dispatch = useAppDispatch()
    const [serverError, setServerError] = useState("")

    const orgComputerizedSystemNumberCompanyWarningLevelSetting = companySettings.find(setting => setting.code === COMPUTERIZED_SYSTEM_NUMBER_COMPANY_WARNING_LEVEL_CODE);
    const orgComputerizedSystemNumberSupplierWarningLevelSetting = companySettings.find(setting => setting.code === COMPUTERIZED_SYSTEM_NUMBER_SUPPLIER_WARNING_LEVEL_CODE);
    const orgComputerizedSystemNumberStructureWarningLevelSetting = companySettings.find(setting => setting.code === COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_WARNING_LEVEL_CODE);
    const orgComputerizedSystemNumberStructureSupplierWarningLevelSetting = companySettings.find(setting => setting.code === COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE);

    const [computerizedSystemNumberCompanyWarningLevelSetting, setComputerizedSystemNumberCompanyWarningLevelSetting] = useState<CompanySetting | undefined>(orgComputerizedSystemNumberCompanyWarningLevelSetting);
    const [computerizedSystemNumberSupplierWarningLevelSetting, setComputerizedSystemNumberSupplierWarningLevelSetting] = useState<CompanySetting | undefined>(orgComputerizedSystemNumberSupplierWarningLevelSetting);
    const [computerizedSystemNumberStructureWarningLevelSetting, setComputerizedSystemNumberStructureWarningLevelSetting] = useState<CompanySetting | undefined>(orgComputerizedSystemNumberStructureWarningLevelSetting);
    const [computerizedSystemNumberStructureSupplierWarningLevelSetting, setComputerizedSystemNumberStructureSupplierWarningLevelSetting] = useState<CompanySetting | undefined>(orgComputerizedSystemNumberStructureSupplierWarningLevelSetting);

    const editableComputerizedSystemWarningSettings = [
        computerizedSystemNumberCompanyWarningLevelSetting,
        computerizedSystemNumberSupplierWarningLevelSetting,
        computerizedSystemNumberStructureWarningLevelSetting,
        computerizedSystemNumberStructureSupplierWarningLevelSetting
    ].filter(Boolean) as CompanySetting[];

    const isDefault = editableComputerizedSystemWarningSettings.length > 0 && editableComputerizedSystemWarningSettings.every(setting => setting.isDefault === true);

    useEffect(() => {
        setComputerizedSystemNumberCompanyWarningLevelSetting(orgComputerizedSystemNumberCompanyWarningLevelSetting);
        setComputerizedSystemNumberSupplierWarningLevelSetting(orgComputerizedSystemNumberSupplierWarningLevelSetting);
        setComputerizedSystemNumberStructureWarningLevelSetting(orgComputerizedSystemNumberStructureWarningLevelSetting);
        setComputerizedSystemNumberStructureSupplierWarningLevelSetting(orgComputerizedSystemNumberStructureSupplierWarningLevelSetting);
    }, [
        companySettings,
        isEditing,
        orgComputerizedSystemNumberCompanyWarningLevelSetting,
        orgComputerizedSystemNumberSupplierWarningLevelSetting,
        orgComputerizedSystemNumberStructureWarningLevelSetting,
        orgComputerizedSystemNumberStructureSupplierWarningLevelSetting
    ]);


    const handleRestore = () => {
        setRestoreDialogOpen(true);
    };
    
    const handleSave = () => {
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setServerError("")
    };

    const handleCloseRestore = () => {
        setRestoreDialogOpen(false);
        setServerError("")
    };
    
    const handleConfirmRestore = async (password: string) => {
        if (password) {
            setSaving(true)
            setServerError("")

            try {
                const result =
                    await dispatch(restoreCompanySettings({
                        password: password,
                        settings: [
                            COMPUTERIZED_SYSTEM_NUMBER_COMPANY_WARNING_LEVEL_CODE,
                            COMPUTERIZED_SYSTEM_NUMBER_SUPPLIER_WARNING_LEVEL_CODE,
                            COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_WARNING_LEVEL_CODE,
                            COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE
                        ]
                    }))

                if (restoreCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings);
                    setComputerizedSystemNumberCompanyWarningLevelSetting(settings.find(f => f.code === COMPUTERIZED_SYSTEM_NUMBER_COMPANY_WARNING_LEVEL_CODE));
                    setComputerizedSystemNumberSupplierWarningLevelSetting(settings.find(f => f.code === COMPUTERIZED_SYSTEM_NUMBER_SUPPLIER_WARNING_LEVEL_CODE));
                    setComputerizedSystemNumberStructureWarningLevelSetting(settings.find(f => f.code === COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_WARNING_LEVEL_CODE));
                    setComputerizedSystemNumberStructureSupplierWarningLevelSetting(settings.find(f => f.code === COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE));
                    setRestoreDialogOpen(false);
                    setIsEditing(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    const handleConfirmSave = async (password: string) => {
        if (password) {
            setSaving(true)
            setServerError("")

            try {
                const updatedSettings: CompanySetting[] = [
                    computerizedSystemNumberCompanyWarningLevelSetting,
                    computerizedSystemNumberSupplierWarningLevelSetting,
                    computerizedSystemNumberStructureWarningLevelSetting,
                    computerizedSystemNumberStructureSupplierWarningLevelSetting
                ].filter(Boolean) as CompanySetting[];

                const result =
                    await dispatch(updateCompanySettings({
                        password: password,
                        settings: updatedSettings
                    }))

                if (updateCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings)
                    setComputerizedSystemNumberCompanyWarningLevelSetting(settings.find(f => f.code === COMPUTERIZED_SYSTEM_NUMBER_COMPANY_WARNING_LEVEL_CODE));
                    setComputerizedSystemNumberSupplierWarningLevelSetting(settings.find(f => f.code === COMPUTERIZED_SYSTEM_NUMBER_SUPPLIER_WARNING_LEVEL_CODE));
                    setComputerizedSystemNumberStructureWarningLevelSetting(settings.find(f => f.code === COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_WARNING_LEVEL_CODE));
                    setComputerizedSystemNumberStructureSupplierWarningLevelSetting(settings.find(f => f.code === COMPUTERIZED_SYSTEM_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE));
                    setDialogOpen(false);
                    setIsEditing(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    const handleComputerizedSystemWarningLevelChange = (
        settingSetter: React.Dispatch<React.SetStateAction<CompanySetting | undefined>>,
        value: WarningLevel
    ) => {
        settingSetter(prev => prev ? { ...prev, value } : prev);
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    return (
        <Card sx={{ maxWidth: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={<Localized id="company-settings-computerized-systems">
                    <span>_Systemy skomputeryzowane</span>
                </Localized>}
                sx={{
                    textAlign: "center",
                    bgcolor: "primary.main",
                    color: "white",
                    py: 1,
                    "& .MuiCardHeader-title": { fontSize: "1rem" },
                }}
            />
            <CardContent>
                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                        primary={<>
                            <Localized id="company-settings-computerized-systems-warning-level-company">
                                <span>_Poziom ostrzegania dla nieunikalnego numeru systemu skomputeryzowanego w firmie</span>
                            </Localized>
                        </>}
                    />
                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!isEditing || computerizedSystemNumberCompanyWarningLevelSetting?.isFixed || !computerizedSystemNumberCompanyWarningLevelSetting}>
                        <InputLabel id="computerized-system-number-company-warning-level-label">
                            <Localized id="company-settings-devices-warning-level">
                                <span>_Poziom ostrzegania</span>
                            </Localized>
                        </InputLabel>
                        <Select
                            labelId="computerized-system-number-company-warning-level-label"
                            value={getWarningLevel(computerizedSystemNumberCompanyWarningLevelSetting)}
                            label={<Localized id="company-settings-devices-warning-level"><span>_Poziom ostrzegania</span></Localized>}
                            onChange={(e) => handleComputerizedSystemWarningLevelChange(setComputerizedSystemNumberCompanyWarningLevelSetting, e.target.value as WarningLevel)}
                        >
                            {COMPUTERIZED_SYSTEM_WARNING_LEVELS.map(level => (
                                <MenuItem key={level} value={level}>
                                    <Localized id={`company-settings-warning-level-${level}`}>{level}</Localized>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ListItem>

                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                        primary={<>
                            <Localized id="company-settings-computerized-systems-warning-level-supplier">
                                <span>_Poziom ostrzegania dla nieunikalnego numeru systemu skomputeryzowanego u dostawcy</span>
                            </Localized>
                        </>}
                    />
                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!isEditing || computerizedSystemNumberSupplierWarningLevelSetting?.isFixed || !computerizedSystemNumberSupplierWarningLevelSetting}>
                        <InputLabel id="computerized-system-number-supplier-warning-level-label">
                            <Localized id="company-settings-devices-warning-level">
                                <span>_Poziom ostrzegania</span>
                            </Localized>
                        </InputLabel>
                        <Select
                            labelId="computerized-system-number-supplier-warning-level-label"
                            value={getWarningLevel(computerizedSystemNumberSupplierWarningLevelSetting)}
                            label={<Localized id="company-settings-devices-warning-level"><span>_Poziom ostrzegania</span></Localized>}
                            onChange={(e) => handleComputerizedSystemWarningLevelChange(setComputerizedSystemNumberSupplierWarningLevelSetting, e.target.value as WarningLevel)}
                        >
                            {COMPUTERIZED_SYSTEM_WARNING_LEVELS.map(level => (
                                <MenuItem key={level} value={level}>
                                    <Localized id={`company-settings-warning-level-${level}`}>{level}</Localized>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ListItem>

                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                        primary={<>
                            <Localized id="company-settings-computerized-systems-warning-level-structure">
                                <span>_Poziom ostrzegania dla nieunikalnego numeru systemu skomputeryzowanego w strukturze</span>
                            </Localized>
                        </>}
                    />
                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!isEditing || computerizedSystemNumberStructureWarningLevelSetting?.isFixed || !computerizedSystemNumberStructureWarningLevelSetting}>
                        <InputLabel id="computerized-system-number-structure-warning-level-label">
                            <Localized id="company-settings-devices-warning-level">
                                <span>_Poziom ostrzegania</span>
                            </Localized>
                        </InputLabel>
                        <Select
                            labelId="computerized-system-number-structure-warning-level-label"
                            value={getWarningLevel(computerizedSystemNumberStructureWarningLevelSetting)}
                            label={<Localized id="company-settings-devices-warning-level"><span>_Poziom ostrzegania</span></Localized>}
                            onChange={(e) => handleComputerizedSystemWarningLevelChange(setComputerizedSystemNumberStructureWarningLevelSetting, e.target.value as WarningLevel)}
                        >
                            {COMPUTERIZED_SYSTEM_WARNING_LEVELS.map(level => (
                                <MenuItem key={level} value={level}>
                                    <Localized id={`company-settings-warning-level-${level}`}>{level}</Localized>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ListItem>

                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                        primary={<>
                            <Localized id="company-settings-computerized-systems-warning-level-structure-supplier">
                                <span>_Poziom ostrzegania dla nieunikalnego numeru systemu skomputeryzowanego w strukturze i u dostawcy</span>
                            </Localized>
                        </>}
                    />
                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!isEditing || computerizedSystemNumberStructureSupplierWarningLevelSetting?.isFixed || !computerizedSystemNumberStructureSupplierWarningLevelSetting}>
                        <InputLabel id="computerized-system-number-structure-supplier-warning-level-label">
                            <Localized id="company-settings-devices-warning-level">
                                <span>_Poziom ostrzegania</span>
                            </Localized>
                        </InputLabel>
                        <Select
                            labelId="computerized-system-number-structure-supplier-warning-level-label"
                            value={getWarningLevel(computerizedSystemNumberStructureSupplierWarningLevelSetting)}
                            label={<Localized id="company-settings-devices-warning-level"><span>_Poziom ostrzegania</span></Localized>}
                            onChange={(e) => handleComputerizedSystemWarningLevelChange(setComputerizedSystemNumberStructureSupplierWarningLevelSetting, e.target.value as WarningLevel)}
                        >
                            {COMPUTERIZED_SYSTEM_WARNING_LEVELS.map(level => (
                                <MenuItem key={level} value={level}>
                                    <Localized id={`company-settings-warning-level-${level}`}>{level}</Localized>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ListItem>

                <Divider sx={{ width: "100%", mt: 3 }} />
                <CardActionButtons
                    isDefault={isDefault}
                    isEditing={isEditing}
                    onRestore={handleRestore}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onEdit={() => setIsEditing(true)}
                />

            </CardContent>
            {dialogOpen &&
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleClose}
                    onConfirm={handleConfirmSave}
                />
            }
            {restoreDialogOpen &&
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleCloseRestore}
                    onConfirm={handleConfirmRestore}
                />
            }
        </Card>

    );
};
